import { Row } from "../styles";
import Input from "../../../../../components/inputField";
import React, { useMemo } from "react";
import { ESubInfo } from "../types";
import { StyledQuestion } from "../../styles";
import { FunctionalButton } from "../../../../../components/functionalButton";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

type JobCostBreakdownInfoSectionProps = {
  esubData: ESubInfo;
  setESubData: (field: string, value: string) => void;
  isMobile: boolean;
  handleNext: () => void;
  panel: number;
  setPanel: (panel: number) => void;
  setIsLastPanel: (isLastPanel: boolean) => void;
  setPreviousStepPanel: (prevStepPanel: number) => void;
};

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

// Validation function to ensure values are between 0 and 100
const validatePercentage = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0 && num <= 100;
};

export const JobCostBreakdownInfoSection = ({
  esubData,
  setESubData,
  isMobile,
  handleNext,
  panel,
  setPanel,
  setIsLastPanel,
  setPreviousStepPanel,
}: JobCostBreakdownInfoSectionProps) => {
  const laborPercent = parseFloat(String(esubData?.bond_details?.labor_percent || "0"));
  const materialPercent = parseFloat(
    String(esubData?.bond_details?.material_percent || "0"),
  );
  const subcontractorPercent = parseFloat(
    String(esubData?.bond_details?.subcontractor_percent || "0"),
  );
  const profitOverheadPercent = parseFloat(
    String(esubData?.bond_details?.profit_overhead_percent || "0"),
  );

  const totalPercent =
    laborPercent +
    materialPercent +
    subcontractorPercent +
    profitOverheadPercent;
  const isValidTotal = Math.abs(totalPercent - 100) < 0.01; 

  const chartData = useMemo(() => {
    const data = [];
    if (laborPercent > 0) {
      data.push({
        name: "Labor",
        value: laborPercent,
        percentage: laborPercent,
      });
    }
    if (materialPercent > 0) {
      data.push({
        name: "Material",
        value: materialPercent,
        percentage: materialPercent,
      });
    }
    if (subcontractorPercent > 0) {
      data.push({
        name: "Subcontractor",
        value: subcontractorPercent,
        percentage: subcontractorPercent,
      });
    }
    if (profitOverheadPercent > 0) {
      data.push({
        name: "Profit/Overhead",
        value: profitOverheadPercent,
        percentage: profitOverheadPercent,
      });
    }
    return data;
  }, [
    laborPercent,
    materialPercent,
    subcontractorPercent,
    profitOverheadPercent,
  ]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: 0 }}>
            <strong>
              {payload[0].payload.name}:{" "}
              {payload[0].payload.percentage.toFixed(1)}%
            </strong>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <StyledQuestion isMobile={isMobile} width="650px">
        What is the Job Cost Breakdown?
      </StyledQuestion>
      <Row>
        <Input
          label="Labor Percent (%)"
          type="number"
          fontSize="18px"
          labelSize="12px"
          value={esubData?.bond_details?.labor_percent ?? ""}
          setValue={(e) => setESubData("bond_details.labor_percent", e)}
          validateFunc={validatePercentage}
          placeholder="Enter percentage (0-100)"
          backgroundColor="white"
          color="#333"
          style={{}}
          withoutMargin={false}
          marginTop="25px"
          id="labor-percent"
          labelWeight="normal"
          textAreaHeight={undefined}
          borderColor={undefined}
          autoComplete={undefined}
          noCheckIcon={false}
          cancelIcon={true}
          noErrorBorder={false}
          ref={undefined}
          hasError={false}
          inputId="labor-percent-input"
          disabled={false}
          width={undefined}
          paddingRight={undefined}
          paddingLeft={undefined}
          onBlur={undefined}
          showError={false}
          readOnly={false}
          marginRight={undefined}
        />
        <Input
          label="Material Percent (%)"
          type="number"
          fontSize="18px"
          labelSize="12px"
          value={esubData?.bond_details?.material_percent ?? ""}
          setValue={(e) => setESubData("bond_details.material_percent", e)}
          validateFunc={validatePercentage}
          placeholder="Enter percentage (0-100)"
          backgroundColor="white"
          color="#333"
          style={{}}
          withoutMargin={false}
          marginTop="25px"
          id="material-percent"
          labelWeight="normal"
          textAreaHeight={undefined}
          borderColor={undefined}
          autoComplete={undefined}
          noCheckIcon={false}
          cancelIcon={true}
          noErrorBorder={false}
          ref={undefined}
          hasError={false}
          inputId="material-percent-input"
          disabled={false}
          width={undefined}
          paddingRight={undefined}
          paddingLeft={undefined}
          onBlur={undefined}
          showError={false}
          readOnly={false}
          marginRight={undefined}
        />
      </Row>

      <Row>
        <Input
          label="Subcontractor Percent (%)"
          type="number"
          fontSize="18px"
          labelSize="12px"
          value={esubData?.bond_details?.subcontractor_percent ?? ""}
          setValue={(e) => setESubData("bond_details.subcontractor_percent", e)}
          validateFunc={validatePercentage}
          placeholder="Enter percentage (0-100)"
          backgroundColor="white"
          color="#333"
          style={{}}
          withoutMargin={false}
          marginTop="25px"
          id="subcontractor-percent"
          labelWeight="normal"
          textAreaHeight={undefined}
          borderColor={undefined}
          autoComplete={undefined}
          noCheckIcon={false}
          cancelIcon={true}
          noErrorBorder={false}
          ref={undefined}
          hasError={false}
          inputId="subcontractor-percent-input"
          disabled={false}
          width={undefined}
          paddingRight={undefined}
          paddingLeft={undefined}
          onBlur={undefined}
          showError={false}
          readOnly={false}
          marginRight={undefined}
        />
        <Input
          label="Profit / Overhead Percent (%)"
          type="number"
          fontSize="18px"
          labelSize="12px"
          value={esubData?.bond_details?.profit_overhead_percent ?? ""}
          setValue={(e) =>
            setESubData("bond_details.profit_overhead_percent", e)
          }
          validateFunc={validatePercentage}
          placeholder="Enter percentage (0-100)"
          backgroundColor="white"
          color="#333"
          style={{}}
          withoutMargin={false}
          marginTop="25px"
          id="profit-overhead-percent"
          labelWeight="normal"
          textAreaHeight={undefined}
          borderColor={undefined}
          autoComplete={undefined}
          noCheckIcon={false}
          cancelIcon={true}
          noErrorBorder={false}
          ref={undefined}
          hasError={false}
          inputId="profit-overhead-percent-input"
          disabled={false}
          width={undefined}
          paddingRight={undefined}
          paddingLeft={undefined}
          onBlur={undefined}
          showError={false}
          readOnly={false}
          marginRight={undefined}
        />
      </Row>

      {/* Pie Chart Section */}
      <div
        style={{
          marginTop: "30px",
          marginBottom: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            marginBottom: "10px",
            color:
              totalPercent > 0
                ? isValidTotal
                  ? "#00C49F"
                  : "#FF8042"
                : "#666",
          }}
        >
          Cost Breakdown Visualization
          {totalPercent > 0 && (
            <span
              style={{
                fontSize: "14px",
                marginLeft: "10px",
                fontWeight: "normal",
              }}
            >
              (Total: {totalPercent.toFixed(1)}%)
            </span>
          )}
        </h3>

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percentage }) =>
                  `${name}: ${percentage.toFixed(1)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div
            style={{
              height: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              fontSize: "14px",
            }}
          >
            Enter percentages above to see the breakdown chart
          </div>
        )}

        {totalPercent > 0 && !isValidTotal && (
          <div
            style={{
              marginTop: "10px",
              padding: "8px 12px",
              backgroundColor: "#ffebee",
              border: "1px solid #ffcdd2",
              borderRadius: "4px",
              color: "#c62828",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            ⚠️ Percentages must total exactly 100% (currently{" "}
            {totalPercent.toFixed(1)}%)
          </div>
        )}
      </div>

      <FunctionalButton
        buttonTitle="Next"
        handleButton={() => {
          handleNext();
          setPanel(7);
        }}
        width={undefined}
        height={undefined}
        isReversedColor={undefined}
        marginRight={undefined}
        marginLeft={undefined}
        marginTop={"20px"}
        marginBottom={undefined}
        fontSize={undefined}
        disabled={!isValidTotal || totalPercent === 0}
        icon={undefined}
        hasBoxShadow={undefined}
        frontIcon={undefined}
        weight={undefined}
        borderColor={undefined}
        selected={undefined}
      />
    </>
  );
};
